// src/services/dashboardService.js
// Single source of truth for Dashboard data (front-end).
// Calls BE /api/dashboard/summary and returns a normalized payload.
// Includes short-TTL memoization and robust param normalization (time-only).

import api from '../api/axios';

/* =============================================================================
   ENV / FEATURE FLAGS
============================================================================= */
const envGet = (craKey, viteKey, def) => {
  let v;
  try {
    if (typeof import.meta !== 'undefined' && import.meta.env && viteKey in import.meta.env) {
      v = import.meta.env[viteKey];
    }
  } catch {}
  try {
    if (v === undefined && typeof process !== 'undefined' && process.env && craKey in process.env) {
      v = process.env[craKey];
    }
  } catch {}
  return v === undefined ? def : v;
};

const ENV = {
  // FE-only fallback is still available, but trimmed to new surface
  FALLBACK_ENABLED: String(
    envGet('REACT_APP_DASHBOARD_FALLBACK', 'VITE_DASHBOARD_FALLBACK', 'false')
  ).toLowerCase() === 'true',

  CACHE_TTL_MS: Number(
    envGet('REACT_APP_DASHBOARD_CACHE_TTL_MS', 'VITE_DASHBOARD_CACHE_TTL_MS', 30_000)
  ),
};

/* =============================================================================
   PARAMS & UTILS
============================================================================= */
const userTZ = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
};

function isIsoDate(s) {
  return /^\d{4}-\d{2}-\d{2}$/.test(String(s || ''));
}
// ⬇️ Accept 1–36 months to match hooks/useDashboardFilters future-proofing
function isRange(s) {
  return /^\d{1,2}m$/i.test(String(s || ''));
}

const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));

const clean = (obj = {}) =>
  Object.fromEntries(
    Object.entries(obj).filter(
      ([, v]) =>
        v !== undefined &&
        v !== null &&
        v !== '' &&
        !(typeof v === 'number' && Number.isNaN(v))
    )
  );

// time-only normalization
function normalizeParams(raw = {}) {
  const tz = raw.tz || userTZ();
  const p = {};

  // Prefer explicit from/to, else range (defaults to 12m)
  if (raw.from || raw.to) {
    if (raw.from && isIsoDate(raw.from)) p.from = raw.from;
    if (raw.to && isIsoDate(raw.to)) p.to = raw.to;
  } else if (raw.range && isRange(raw.range)) {
    const m = raw.range.match(/^(\d{1,2})m$/i);
    if (m) {
      const n = clamp(parseInt(m[1], 10), 1, 36);
      p.range = `${n}m`;
    }
  } else {
    p.range = '12m';
  }

  p.tz = tz; // advisory; BE resolves company TZ authoritatively
  return p;
}

function cfg({ params, tz, signal } = {}) {
  return {
    params: clean(params),
    // headers: { 'X-TZ': tz || userTZ() }, // optional hint; BE reads query.tz (but uses company TZ)
    signal,
  };
}

/* =============================================================================
   SHAPE NORMALIZATION
============================================================================= */
function ensureShape(payload) {
  // ✅ Preserve currency/company fields from BE so Dashboard.jsx can use company preferred currency (CAD etc.)
  const preferredCurrency =
    payload?.currency ||
    payload?.companyCurrency ||
    payload?.filters?.currency ||
    payload?.company?.billingCurrency ||
    payload?.company?.defaultInvoiceCurrency ||
    payload?.company?.currency ||
    payload?.company?.preferredCurrency ||
    null;

  const base = {
    // ✅ pass-through fields (were previously dropped by normalization)
    currency: preferredCurrency,
    companyCurrency: payload?.companyCurrency || payload?.currency || null,
    company: payload?.company || null,

    filters: {
      from: null,
      to: null,
      tz: userTZ(),
      ...(payload?.filters || {}),
    },
    // KPI set (now includes jobsScheduled + jobsInProgress)
    kpis: {
      cashCollected: 0,
      openAR: 0,
      quotesAwaiting: 0,
      winRate: 0,
      jobsScheduled: 0,
      jobsInProgress: 0,
      ...(payload?.kpis || {}),
    },
    revenueTrend: Array.isArray(payload?.revenueTrend) ? payload.revenueTrend : [],
    invoiceStatus: {
      paid: 0,
      partial: 0,
      unpaid: 0,
      overdue: 0,
      ...(payload?.invoiceStatus || {}),
    },
    arAging: {
      b0_30: 0,
      b31_60: 0,
      b61_90: 0,
      b90_plus: 0,
      dso: 0,
      ...(payload?.arAging || {}),
    },
    // kept for backward compat (even if the UI no longer shows all lists)
    needsAttention: {
      overdueInvoices: [],
      expiringQuotes: [],
      ...(payload?.needsAttention || {}),
    },
    // NEW: do not drop teamWorkload coming from the BE
    teamWorkload: Array.isArray(payload?.teamWorkload) ? payload.teamWorkload : [],
    freshness: {
      generatedAt: new Date().toISOString(),
      cacheTtlSeconds: 0,
      ...(payload?.freshness || {}),
    },
    errors: Array.isArray(payload?.errors) ? payload.errors : [],
  };

  // Defensive numeric coercions
  const num = (x) => (typeof x === 'number' ? x : Number(x || 0));
  base.kpis.cashCollected = num(base.kpis.cashCollected);
  base.kpis.openAR = num(base.kpis.openAR);
  base.kpis.quotesAwaiting = num(base.kpis.quotesAwaiting);
  base.kpis.winRate = Number.isFinite(base.kpis.winRate) ? base.kpis.winRate : 0;
  base.kpis.jobsScheduled = num(base.kpis.jobsScheduled);
  base.kpis.jobsInProgress = num(base.kpis.jobsInProgress);

  base.invoiceStatus.paid = num(base.invoiceStatus.paid);
  base.invoiceStatus.partial = num(base.invoiceStatus.partial);
  base.invoiceStatus.unpaid = num(base.invoiceStatus.unpaid);
  base.invoiceStatus.overdue = num(base.invoiceStatus.overdue);

  base.arAging.b0_30 = num(base.arAging.b0_30);
  base.arAging.b31_60 = num(base.arAging.b31_60);
  base.arAging.b61_90 = num(base.arAging.b61_90);
  base.arAging.b90_plus = num(base.arAging.b90_plus);
  base.arAging.dso = Number.isFinite(base.arAging.dso) ? base.arAging.dso : 0;

  // Sort trend ascending by month (YYYY-MM) and normalize keys
  if (base.revenueTrend.length) {
    base.revenueTrend = [...base.revenueTrend]
      .sort((a, b) => String(a.month || a.period).localeCompare(String(b.month || b.period)))
      .map((m) => ({
        month: String(m.month || m.period || ''),
        booked: num(m.booked),
        collected: num(m.collected ?? m.amount),
      }));
  }

  // Normalize teamWorkload items
  if (base.teamWorkload.length) {
    base.teamWorkload = base.teamWorkload.map((x) => ({
      userId: x?.userId != null ? String(x.userId) : x?._id ? String(x._id) : null,
      name: x?.name || x?.user?.name || 'Unassigned',
      avatarUrl: x?.avatarUrl || x?.user?.avatarUrl || null,
      counts: {
        underway: num(x?.counts?.underway ?? x?.underway),
        today: num(x?.counts?.today ?? x?.today),
        next7: num(x?.counts?.next7 ?? x?.next7),
        next30: num(x?.counts?.next30 ?? x?.next30),
        overdue: num(x?.counts?.overdue ?? x?.overdue),
      },
    }));
  }

  return base;
}

/* =============================================================================
   IN-MEMO CACHE
============================================================================= */
const _cache = new Map(); // key -> { expiresAt:number, value:any, inflight?:Promise }

function makeKey(params) {
  return JSON.stringify(params);
}

function getCached(key) {
  const v = _cache.get(key);
  if (!v) return null;
  if (v.expiresAt < Date.now()) {
    _cache.delete(key);
    return null;
  }
  return v.value;
}

function setCached(key, value, ttlMs = ENV.CACHE_TTL_MS) {
  _cache.set(key, { value, expiresAt: Date.now() + ttlMs });
}

function getInflight(key) {
  const v = _cache.get(key);
  return v?.inflight || null;
}

function setInflight(key, promise) {
  const v = _cache.get(key) || {};
  v.inflight = promise;
  _cache.set(key, v);
}

function clearInflight(key) {
  const v = _cache.get(key);
  if (v) {
    delete v.inflight;
    _cache.set(key, v);
  }
}

/* =============================================================================
   BACKEND CALL + (optional) CLIENT FALLBACK
============================================================================= */
async function callBackend(params, signal) {
  const tz = params.tz || userTZ();
  const response = await api.get('/dashboard/summary', cfg({ params, tz, signal }));
  return ensureShape(response?.data || {});
}

/** Best-effort fallback (disabled by default), trimmed to new surface. */
async function composeClientFallback(params, signal) {
  const safeGet = async (url, qs = {}) => {
    try {
      const { data } = await api.get(url, cfg({ params: qs, tz: params.tz, signal }));
      return data || {};
    } catch {
      return {};
    }
  };

  const { from, to, range, tz } = params;
  const baseQs = clean({ from, to, range, tz });

  const [paymentsRes, invoicesRes, quotesRes, worksRes] = await Promise.all([
    safeGet('/payments', baseQs),
    safeGet('/invoices', baseQs),
    safeGet('/quotes', baseQs),
    safeGet('/works', baseQs),
  ]);

  const arr = (x) => (Array.isArray(x) ? x : Array.isArray(x?.items) ? x.items : []);
  const payments = arr(paymentsRes);
  const invoices = arr(invoicesRes);
  const quotes = arr(quotesRes);
  const works = arr(worksRes);

  const sum = (xs, f) => xs.reduce((t, x) => t + (Number(f(x)) || 0), 0);

  // ✅ best-effort currency inference for fallback mode
  const inferredCurrency =
    paymentsRes?.currency ||
    invoicesRes?.currency ||
    quotesRes?.currency ||
    invoices?.[0]?.currency ||
    quotes?.[0]?.currency ||
    null;

  // KPIs
  const cashCollected = sum(payments, (p) => p.amount);
  const now = new Date();
  const remaining = (inv) =>
    Number(inv.balanceDue ?? (Number(inv.totalAmount || 0) - Number(inv.totalPaid || 0))) || 0;

  const openAR = sum(
    invoices.filter((i) =>
      ['unpaid', 'partial', 'overdue'].includes(String(i.status || '').toLowerCase())
    ),
    (i) => remaining(i)
  );

  const quotesAwaiting = quotes.filter((q) =>
    ['sent', 'awaiting', 'pending'].includes(
      String(q.lifecycleStatus || q.status || '').toLowerCase()
    )
  ).length;

  const wins = quotes.filter((q) =>
    ['accepted', 'won'].includes(String(q.lifecycleStatus || q.status || '').toLowerCase())
  ).length;
  const losses = quotes.filter((q) =>
    ['declined', 'expired', 'lost'].includes(String(q.lifecycleStatus || q.status || '').toLowerCase())
  ).length;
  const winRate = wins + losses > 0 ? wins / (wins + losses) : 0;

  const jobsInProgress = works.filter((w) =>
    ['scheduled', 'in_progress'].includes(String(w.status || '').toLowerCase())
  ).length;

  // Very light teamWorkload approximation (single-owner)
  const by = new Map();
  const normalizeId = (w) => (Array.isArray(w.assignees) && w.assignees[0]) || w.assignedTo || null;
  const isUnderway = (s) => /^(en[_ ]?route|in[_ ]?progress|on[_ ]?site)$/i.test(String(s || ''));
  const isClosed = (s) => /^(completed|canceled|cancelled)$/i.test(String(s || ''));
  const start = (w) => new Date(w?.schedule?.start || 0);
  const end = (w) => new Date(w?.schedule?.end || 0);
  const sameDay = (a, b) =>
    a.getUTCFullYear() === b.getUTCFullYear() &&
    a.getUTCMonth() === b.getUTCMonth() &&
    a.getUTCDate() === b.getUTCDate();
  const addDays = (d, n) => new Date(d.getTime() + n * 86400000);

  const todayStart = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  const todayEnd = addDays(todayStart, 1);

  for (const w of works) {
    const uid = normalizeId(w);
    const key = uid ? String(uid) : 'unassigned';
    if (!by.has(key)) by.set(key, { underway: 0, today: 0, next7: 0, next30: 0, overdue: 0 });

    const s = String(w.status || '');
    const st = start(w);
    const en = end(w);
    const due = w.dueDate ? new Date(w.dueDate) : en;

    if (isUnderway(s)) by.get(key).underway += 1;
    if (/^(scheduled|confirmed)$/i.test(s)) {
      if (st && sameDay(st, todayStart)) by.get(key).today += 1;
      else if (st > todayEnd && st <= addDays(todayStart, 7)) by.get(key).next7 += 1;
      else if (st > addDays(todayStart, 7) && st <= addDays(todayStart, 30)) by.get(key).next30 += 1;
    }
    if (!isClosed(s) && due && due < now) by.get(key).overdue += 1;
  }

  const teamWorkload = Array.from(by.entries()).map(([k, v]) => ({
    userId: k === 'unassigned' ? null : k,
    name: k === 'unassigned' ? 'Unassigned' : 'User',
    avatarUrl: null,
    counts: v,
  }));

  // Invoice status counts
  const invoiceStatus = { paid: 0, partial: 0, unpaid: 0, overdue: 0 };
  invoices.forEach((i) => {
    const s = String(i.status || '').toLowerCase();
    if (invoiceStatus[s] != null) invoiceStatus[s] += 1;
  });

  // Needs Attention (lists we keep)
  const overdue = invoices.filter(
    (i) =>
      new Date(i.dueDate || 0) < now &&
      String(i.status || '').toLowerCase() !== 'paid' &&
      remaining(i) > 0
  );
  const needsAttention = {
    overdueInvoices: overdue
      .slice()
      .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate))
      .slice(0, 50)
      .map((i) => ({
        invoiceId: String(i._id || ''),
        invoiceNumber: i.invoiceNumber || 'INV',
        clientName: i.client?.name || i.clientName || '(Unknown)',
        dueDate: i.dueDate || null,
        remainingAmount: remaining(i),
        ownerId: i.assignedTo || i.ownerId || null,
      })),
    expiringQuotes: quotes
      .filter((q) => {
        const s = String(q.lifecycleStatus || q.status || '').toLowerCase();
        const isWaiting = ['sent', 'awaiting', 'pending'].includes(s);
        const ex = q.expiryDate || q.expiresAt;
        return isWaiting && ex && new Date(ex) <= new Date(Date.now() + 7 * 86400000);
      })
      .slice()
      .sort((a, b) => new Date(a.expiryDate || a.expiresAt) - new Date(b.expiryDate || b.expiresAt))
      .slice(0, 50)
      .map((q) => ({
        quoteId: String(q._id || ''),
        quoteNumber: q.quoteNumber || 'Q',
        clientName: q.client?.name || q.clientName || '(Unknown)',
        expiresAt: q.expiryDate || q.expiresAt || null,
        amount: Number(q.totalAmount || 0),
        ownerId: q.assignedTo || q.ownerId || null,
      })),
  };

  return ensureShape({
    // ✅ include inferred currency in fallback payload
    currency: inferredCurrency,

    filters: { ...params },
    kpis: {
      cashCollected,
      openAR,
      quotesAwaiting,
      winRate,
      jobsScheduled: 0, // not computed in FE fallback
      jobsInProgress,
    },
    revenueTrend: [], // not computed in FE fallback
    invoiceStatus,
    arAging: { b0_30: 0, b31_60: 0, b61_90: 0, b90_plus: 0, dso: 0 }, // not computed in FE fallback
    needsAttention,
    teamWorkload,
    freshness: { generatedAt: new Date().toISOString(), cacheTtlSeconds: 0 },
    errors: [{ scope: 'fallback', message: 'Using client-side fallback; values approximated.' }],
  });
}

/* =============================================================================
   PUBLIC API
============================================================================= */
export async function getDashboardSummary(opts = {}) {
  const params = normalizeParams(opts);
  const signal = opts.signal;

  const cacheKey = makeKey(params);
  if (!opts.noCache) {
    const cached = getCached(cacheKey);
    if (cached) return cached;

    const inflight = getInflight(cacheKey);
    if (inflight) return inflight;
  }

  const run = (async () => {
    try {
      const data = await callBackend(params, signal);
      setCached(cacheKey, data);
      return data;
    } catch (err) {
      const status = err?.response?.status;
      const retriable = status === 404 || status === 501;
      if (ENV.FALLBACK_ENABLED && retriable) {
        const fb = await composeClientFallback(params, signal);
        setCached(cacheKey, fb);
        return fb;
      }
      const error = new Error(err?.response?.data?.error || err?.message || 'Failed to load dashboard');
      error.status = status || 500;
      error.detail = err?.response?.data?.detail || null;
      throw error;
    } finally {
      clearInflight(cacheKey);
    }
  })();

  if (!opts.noCache) setInflight(cacheKey, run);
  return run;
}

// Optional alias (useful if other code imports fetchDashboardSummary)
export async function fetchDashboardSummary(opts = {}) {
  return getDashboardSummary(opts);
}

export function toSearchParams(raw = {}) {
  const p = normalizeParams(raw);
  const qs = new URLSearchParams(clean(p));
  return qs.toString();
}

export default { getDashboardSummary, fetchDashboardSummary, toSearchParams };
